import { useMutation } from "@apollo/react-hooks";
import {
    Box,
    Button,
    Grid,
    InputAdornment,
    makeStyles,
    MenuItem,
    Select,
    Theme,
    Typography,
} from "@material-ui/core";
import MyPhoneNumber from "material-ui-phone-number";
import { Checkboxes, ShowErrorProps, TextField } from "mui-rff";
import React, { useEffect, useState } from "react";
import { useAlert } from "react-alert";
import { Form, FormSpy } from "react-final-form";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import { Prompt, useHistory } from "react-router-dom";
import { Dispatch } from "redux";
import MirrorRestaurantLabel from "../../components/common/MirrorRestaurantLabel";
import ImageUpload from "../../components/ImageUpload";
import { EntitySaveButton } from "../../components/menu/EntitySaveButton";
import {
    RestaurantBranding,
    RestaurantContactInfo,
    RestaurantSettings,
} from "../../generated-interfaces/graphql";
import { UploadMutation } from "../../queries/imageUpload";
import {
    createTREnablement,
    createTTSScheduler,
    updateAIEnablement,
    updatePrimaryRestaurant,
    updateRestaurantBranding,
    updateRestaurantContactInfo,
    updateRestaurantSettings,
    updateTREnablement,
    updateTTSScheduler,
} from "../../reducers/restaurantReducer";
import { RootState } from "../../reducers/rootReducer";
import {
    restaurantInfoSelector,
    restaurantsByUserRoleSelector,
    selectIsAIEnabled,
    selectIsTREnabled,
    selectIsTTSSchedulerEnabled,
    selectIsUnsupervisedAI,
} from "../../selectors/restaurant";
import { Restaurant } from "../../types/restaurant";
import { TIME_OUT } from "../../utils/constants";
import {
    CallbackFunction,
    RestaurantUpdateDetails,
    StringOrNull,
} from "../../utils/types";
import PrestoVoiceConfiguration from "./PrestoVoiceConfiguration";

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        padding: theme.spacing(8, 4, 8, 4),
        backgroundColor: "#fff",
    },
    header: {
        paddingBottom: theme.spacing(3),
    },
    taxHeading: {
        fontWeight: "bold",
    },
    taxGroup: {
        marginTop: "4px",
        "& .MuiInputBase-root": {
            width: "88px",
            "& .MuiInputBase-input": {
                textAlign: "right",
                fontSize: "0.875rem",
            },
            "& .MuiInputAdornment-positionEnd": {
                marginLeft: "2px",
                "& .MuiTypography-body1": {
                    marginRight: "8px",
                },
            },
        },
    },
    error: {
        fontSize: "0.75rem",
        textAlign: "left",
        lineHeight: 1.66,
        margin: "-5px 14px 0",
        color: "#f50057",
    },
    operationHoursButton: {
        marginLeft: 10,
    },
}));

const RestaurantTaxRate = ({
    title,
    inputName,
    errors,
}: {
    inputName: string;
    title: string;
    errors: Record<string, string>;
}) => {
    const classes = useStyles();
    return (
        <Grid
            container
            item
            xs={12}
            alignItems="center"
            className={classes.taxGroup}
        >
            <Grid item xs={9}>
                <Typography>{title}</Typography>
            </Grid>
            <Grid item xs={3}>
                <TextField
                    fullWidth={false}
                    className="custom-input-gray"
                    type="text"
                    name={inputName}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                        ),
                        inputProps: { min: 0 },
                        disableUnderline: true,
                    }}
                    //Should return false to display custom error due design limitation
                    showError={() => false}
                />
            </Grid>
            {errors[inputName] && (
                <p className={classes.error}>{errors[inputName]}</p>
            )}
        </Grid>
    );
};

function RestaurantSetting(props: Props) {
    const classes = useStyles();
    const history = useHistory();
    const alert = useAlert();
    const dispatch = useDispatch();

    const [blockNavigation, setBlockNavigation] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const restaurantInfo = useSelector(restaurantInfoSelector);
    const [logoUrl, setLogoUrl] = useState<StringOrNull>(null);
    const [backgroundImage, setBackgroundImage] = useState<StringOrNull>(null);
    const [uploadBackgroundImage, uploadBackgroundImageResult] = useMutation(
        UploadMutation
    );
    const [uploadBrandLogo, uploadBrandLogoResult] = useMutation(
        UploadMutation
    );
    const [backgroundImageStatus, setBackgroundImageStatus] = useState("");
    const [brandLogoStatus, setBrandLogoStatus] = useState("");
    const [isSavingError, setSavingError] = useState(false);
    const [primaryRestaurantCode, setPrimaryRestaurantCode] = useState("");
    const restaurantsByUserRole = useSelector(restaurantsByUserRoleSelector);
    const [restaurantsMap, setRestaurantsMap] = useState<
        Record<string, Restaurant>
    >({});
    useEffect(() => {
        window.onbeforeunload = null;
    }, []);

    const isTREnabled = useSelector(selectIsTREnabled);
    const [tempTREnabled, updateTempTREnabled] = useState(0);

    useEffect(() => {
        //If the record is not present, it will be null - so keeping a default value of 0
        updateTempTREnabled(isTREnabled || 0);
    }, [isTREnabled]);

    const isTTSSchedulerEnabled = useSelector(selectIsTTSSchedulerEnabled);
    const [tempTTSScheduler, updateTempTTSScheduler] = useState(false);

    useEffect(() => {
        //If the record is not present, it will be null - so keeping a default value of false
        updateTempTTSScheduler(isTTSSchedulerEnabled || false);
    }, [isTTSSchedulerEnabled]);

    const isEnableAI = useSelector(selectIsAIEnabled);
    const [tempEnableAI, updateTempEnableAI] = useState(false);

    useEffect(() => {
        updateTempEnableAI(isEnableAI || false);
    }, [isEnableAI]);

    const isUnsupervisedAI = useSelector(selectIsUnsupervisedAI);
    const [tempIsUnsupervisedAI, updateTempIsUnsupervisedAI] = useState(false);

    useEffect(() => {
        //If the record is not present, it will be null - so keeping a default value of false
        updateTempIsUnsupervisedAI(isUnsupervisedAI || false);
    }, [isUnsupervisedAI]);

    useEffect(() => {
        if (restaurantInfo?.restaurantContactInfo?.phoneNumber) {
            setPhoneNumber(restaurantInfo.restaurantContactInfo.phoneNumber);
        }
        setPrimaryRestaurantCode(restaurantInfo?.primaryRestaurantCode || "");
        if (restaurantInfo?.restaurantBranding) {
            setLogoUrl(restaurantInfo.restaurantBranding.logoUrl);
            setBackgroundImage(
                restaurantInfo.restaurantBranding.backgroundImageUrl
            );
        }
    }, [restaurantInfo]);

    useEffect(() => {
        if (
            (brandLogoStatus === "removed" || brandLogoStatus === "") &&
            (backgroundImageStatus === "removed" ||
                backgroundImageStatus === "")
        )
            setImageLoaded(false);
    }, [brandLogoStatus, backgroundImageStatus]);

    const initialValues: any = {
        ...restaurantInfo?.restaurantContactInfo,
        ...restaurantInfo?.restaurantSettings,
        isTREnabled: tempTREnabled,
        tempTTSScheduler: tempTTSScheduler,
        tempEnableAI,
    };

    initialValues.website =
        initialValues?.website && initialValues.website.replace("http://", "");

    const handleUploadBackgroundImage = (file: any) => {
        uploadBackgroundImage({
            variables: { file, restaurantCode: props.restaurantCode },
        });
        setImageLoaded(true);
    };

    const handleUploadBrandLogo = (file: any) => {
        uploadBrandLogo({
            variables: { file, restaurantCode: props.restaurantCode },
        });
        setImageLoaded(true);
    };

    const changeTRenablement = (newValue: number) => {
        updateTempTREnabled(newValue);
    };

    const changeTTSScheduler = (newValue: boolean) => {
        updateTempTTSScheduler(newValue);
    };

    const changeEnableAI = (newValue: boolean) => {
        updateTempEnableAI(newValue);
    };

    const changeIsUnsupervisedAI = (newValue: boolean) => {
        updateTempIsUnsupervisedAI(newValue);
    };

    const onSubmit = (data: any) => {
        if (
            backgroundImageStatus === "done" &&
            uploadBackgroundImageResult.called === false
        ) {
            alert.info("Select Replace Image button of background image", {
                timeout: TIME_OUT,
            });
            return;
        } else if (
            brandLogoStatus === "done" &&
            uploadBrandLogoResult.called === false
        ) {
            alert.info("Select Replace Image button of logo", {
                timeout: TIME_OUT,
            });
            return;
        }

        if (primaryRestaurantCode !== restaurantInfo?.primaryRestaurantCode)
            props.updatePrimaryRestaurant({
                primaryRestaurantCode: primaryRestaurantCode,
                errorCallback: () => {
                    setSavingError(true);
                },
            });

        props.updateRestaurantSettings({
            dineInModalityTaxRate: parseFloat(data.dineInModalityTaxRate || 0),
            deliveryModalityTaxRate: parseFloat(
                data.deliveryModalityTaxRate || 0
            ),
            toGoModalityTaxRate: parseFloat(data.toGoModalityTaxRate || 0),
            toGoOrderConfirmationMessage: data.toGoOrderConfirmationMessage,
            toGoDefaultSMSText: data.toGoDefaultSMSText,
            toGoPickupMinutes: parseInt(data.toGoPickupMinutes),
            isUnsupervisedAI: tempIsUnsupervisedAI,
            errorCallback: () => {
                setSavingError(true);
            },
            isAlertingEnabled: data.isAlertingEnabled,
        } as any);

        const {
            address = "",
            city = "",
            zipCode = "",
            state = "",
            country = "",
            email = "",
        } = data;

        props.updateRestaurantContactInfo({
            ...data,
            address,
            city,
            zipCode,
            state,
            country,
            email,
            phoneNumber,
            website: data.website ? "http://" + data.website : null,
            errorCallback: () => {
                setSavingError(true);
            },
        });

        props.updateRestaurantBranding({
            logoUrl: uploadBrandLogoResult.data?.uploadImage || logoUrl || null,
            backgroundImageUrl:
                uploadBackgroundImageResult.data?.uploadImage ||
                backgroundImage ||
                null,
            successCallback: () => {
                if (isSavingError) {
                    alert.error("Error Saving Restaurant Settings", {
                        timeout: TIME_OUT,
                    });
                } else {
                    setBlockNavigation(false);
                    setImageLoaded(false);
                    alert.success("Restaurant Settings Saved", {
                        timeout: TIME_OUT,
                    });
                }
            },
            errorCallback: () => {
                alert.error("Error Saving Restaurant Settings", {
                    timeout: TIME_OUT,
                });
            },
        } as any);

        //Check if Task Router Enablement is changed
        if (tempTREnabled !== isTREnabled) {
            const updateFunction =
                isTREnabled !== null ? updateTREnablement : createTREnablement;
            dispatch(updateFunction({ active: tempTREnabled }));
        }

        //Check if TTScheduler is changed
        if (tempTTSScheduler !== isTTSSchedulerEnabled) {
            const updateFunction =
                isTTSSchedulerEnabled === null
                    ? createTTSScheduler
                    : updateTTSScheduler;
            dispatch(updateFunction({ scheduler_enabled: tempTTSScheduler }));
        }

        //Check if AI Enablement is changed
        if (tempEnableAI !== isEnableAI) {
            dispatch(
                updateAIEnablement({
                    aiActive: tempEnableAI,
                    successCallback: () => {
                        alert.success("Saved AI ON/OFF settings", {
                            timeout: TIME_OUT,
                        });
                    },
                    errorCallback: () => {
                        //When error occured during making AI ON/OFF,
                        alert.error("Toggle of AI ON/OFF Failed", {
                            timeout: TIME_OUT,
                        });
                        updateTempEnableAI(isEnableAI);
                    },
                })
            );
        }
    };

    const showHoursOfOperation = () => {
        history.push(
            `/${restaurantInfo?.restaurantCode}/restaurant-settings/operation-hours`
        );
    };

    const onShowError = (errorProp: ShowErrorProps): boolean =>
        !!errorProp?.meta?.error;

    useEffect(() => {
        setRestaurantsMap(
            restaurantsByUserRole.reduce((a, c) => {
                c.restaurants.forEach((r) => {
                    if (!(r.restaurantCode in a) && !r.primaryRestaurantCode) {
                        a[r.restaurantCode] = r;
                    }
                });
                return a;
            }, {} as Record<string, Restaurant>)
        );
    }, [restaurantsByUserRole]);

    return (
        <div className={classes.container}>
            <MirrorRestaurantLabel />
            <Prompt
                when={blockNavigation || imageLoaded}
                message={(location, action) => {
                    console.log("action", action);
                    return "The changes you've made will be lost. Are you sure you want to discard the changes?";
                }}
            />

            <Form
                onSubmit={onSubmit}
                initialValues={initialValues}
                render={({ handleSubmit, values, valid, form, errors }) => (
                    <form onSubmit={handleSubmit}>
                        <FormSpy
                            onChange={(props) => {
                                setBlockNavigation(props.dirty);
                            }}
                        />
                        <EntitySaveButton
                            disabled={
                                uploadBackgroundImageResult.loading ||
                                uploadBrandLogoResult.loading
                            }
                            style={{ float: "right" }}
                        />
                        <Typography
                            variant="h6"
                            component="h4"
                            color="primary"
                            className={classes.header}
                        >
                            Restaurant Settings
                        </Typography>
                        <Typography variant="h6" color="primary">
                            Contact Information
                        </Typography>
                        <Grid container style={{ maxWidth: "600px" }}>
                            <Grid item xs={12}>
                                <TextField
                                    name="address"
                                    placeholder="Number & Street"
                                    label="Street"
                                    fullWidth
                                    margin="normal"
                                    variant="filled"
                                    InputProps={{
                                        disableUnderline: true,
                                    }}
                                    showError={onShowError}
                                />
                            </Grid>
                            <Grid item xs={6} style={{ paddingRight: "10px" }}>
                                <TextField
                                    name="city"
                                    placeholder="City"
                                    label="City"
                                    fullWidth
                                    margin="normal"
                                    variant="filled"
                                    InputProps={{
                                        disableUnderline: true,
                                    }}
                                    showError={onShowError}
                                />
                            </Grid>
                            <Grid item xs={6} style={{ paddingLeft: "10px" }}>
                                <TextField
                                    name="zipCode"
                                    placeholder="ZiP Code"
                                    label="Zip Code"
                                    fullWidth
                                    margin="normal"
                                    variant="filled"
                                    InputProps={{
                                        disableUnderline: true,
                                    }}
                                    showError={onShowError}
                                />
                            </Grid>
                            <Grid item xs={6} style={{ paddingRight: "10px" }}>
                                <TextField
                                    name="state"
                                    placeholder="State"
                                    label="State"
                                    fullWidth
                                    margin="normal"
                                    variant="filled"
                                    InputProps={{
                                        disableUnderline: true,
                                    }}
                                    showError={onShowError}
                                />
                            </Grid>
                            <Grid item xs={6} style={{ paddingLeft: "10px" }}>
                                <TextField
                                    name="country"
                                    placeholder="Country"
                                    label="Country"
                                    fullWidth
                                    margin="normal"
                                    variant="filled"
                                    InputProps={{
                                        disableUnderline: true,
                                    }}
                                    showError={onShowError}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <MyPhoneNumber
                                    label="Phone Number"
                                    defaultCountry="us"
                                    value={phoneNumber || ""}
                                    onChange={(value: any) =>
                                        setPhoneNumber(value as string)
                                    }
                                    fullWidth
                                    margin="normal"
                                    variant="filled"
                                    InputProps={{
                                        disableUnderline: true,
                                    }}
                                />
                                {errors.phoneNumber && (
                                    <p className={classes.error}>
                                        {errors.phoneNumber}
                                    </p>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    type="email"
                                    name="email"
                                    placeholder="Email"
                                    label="Email"
                                    fullWidth
                                    margin="normal"
                                    variant="filled"
                                    InputProps={{
                                        disableUnderline: true,
                                    }}
                                    showError={onShowError}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="website"
                                    placeholder="Website"
                                    label="Website"
                                    fullWidth
                                    margin="normal"
                                    variant="filled"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                http://
                                            </InputAdornment>
                                        ),
                                        disableUnderline: true,
                                    }}
                                    showError={onShowError}
                                />
                            </Grid>
                        </Grid>

                        <PrestoVoiceConfiguration
                            restaurantInfo={restaurantInfo}
                            restaurantsMap={restaurantsMap}
                            primaryRestaurantCode={primaryRestaurantCode}
                            setPrimaryRestaurantCode={setPrimaryRestaurantCode}
                            isTREnabled={values.isTREnabled}
                            toggleEnableTR={changeTRenablement}
                            tempTTSScheduler={values.tempTTSScheduler}
                            changeTTSScheduler={changeTTSScheduler}
                            tempEnableAI={values.tempEnableAI}
                            changeEnableAI={changeEnableAI}
                            tempIsUnsupervisedAI={tempIsUnsupervisedAI}
                            changeIsUnsupervisedAI={changeIsUnsupervisedAI}
                        />

                        <Typography
                            variant="h6"
                            color="primary"
                            style={{ margin: "24px 0 16px 0" }}
                        >
                            Configuration
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6} md={8}>
                                <ImageUpload
                                    isImageUploading={
                                        uploadBrandLogoResult.loading
                                    }
                                    header="Restaurant Logo"
                                    title="Add your logo for the guest web app."
                                    imageUrl={logoUrl}
                                    uploadImage={handleUploadBrandLogo}
                                    deleteOriginalImage={() => setLogoUrl(null)}
                                    setImageLoadingStatus={setBrandLogoStatus}
                                />
                            </Grid>
                        </Grid>
                        <ImageUpload
                            isImageUploading={
                                uploadBackgroundImageResult.loading
                            }
                            header="Background Image"
                            title="Add a background image for the guest web app."
                            imageUrl={backgroundImage}
                            uploadImage={handleUploadBackgroundImage}
                            deleteOriginalImage={() => setBackgroundImage(null)}
                            setImageLoadingStatus={setBackgroundImageStatus}
                        />

                        <div style={{ marginTop: "20px", maxWidth: "600px" }}>
                            <div>
                                <Typography className={classes.taxHeading}>
                                    Tax Rate
                                </Typography>
                                <RestaurantTaxRate
                                    errors={errors}
                                    inputName="dineInModalityTaxRate"
                                    title="Dine In Tax (%)"
                                />
                                <RestaurantTaxRate
                                    errors={errors}
                                    inputName="toGoModalityTaxRate"
                                    title="To-Go Tax (%)"
                                />
                                <RestaurantTaxRate
                                    errors={errors}
                                    inputName="deliveryModalityTaxRate"
                                    title="Delivery Tax (%)"
                                />
                            </div>
                        </div>
                        <Grid item xs={6}>
                            <Checkboxes
                                color="primary"
                                name="isAlertingEnabled"
                                placeholder="Show alert for returning customers"
                                data={{
                                    label: "Show alert for returning customers",
                                    value: true,
                                }}
                            />
                        </Grid>

                        <div style={{ marginTop: "20px" }}>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={showHoursOfOperation}
                                disableElevation
                                type="button"
                            >
                                Edit Hours of Operation
                            </Button>
                        </div>

                        <Box mt={2}>
                            <Grid container>
                                <Grid item xs={12}>
                                    <Typography variant="h6" color="primary">
                                        To-Go Settings
                                    </Typography>
                                </Grid>
                                <Grid
                                    container
                                    alignItems="center"
                                    item
                                    xs={12}
                                >
                                    <Grid item xs={6} md={3}>
                                        <Typography>
                                            Earliest pickup time
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3} md={3}>
                                        <Select
                                            fullWidth={false}
                                            className="custom-input-gray"
                                            type="text"
                                            value={values.toGoPickupMinutes}
                                            onChange={(e) => {
                                                form.change(
                                                    "toGoPickupMinutes",
                                                    parseInt(
                                                        e.target.value as string
                                                    )
                                                );
                                            }}
                                            disableUnderline
                                        >
                                            {Array.from(Array(37).keys()).map(
                                                (i) => {
                                                    return (
                                                        <MenuItem
                                                            key={i}
                                                            value={i * 5}
                                                        >
                                                            {i * 5} mins
                                                        </MenuItem>
                                                    );
                                                }
                                            )}
                                        </Select>
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    alignItems="center"
                                    item
                                    xs={12}
                                >
                                    <Grid item xs={6} md={6}>
                                        <TextField
                                            fullWidth={true}
                                            type="text"
                                            variant="filled"
                                            rows={4}
                                            multiline
                                            name="toGoDefaultSMSText"
                                            InputProps={{
                                                disableUnderline: true,
                                            }}
                                            inputProps={{
                                                maxLength: 100,
                                            }}
                                            margin="normal"
                                            label="Default SMS Notification Text"
                                            placeholder="Your order is ready for pickup."
                                        />
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    alignItems="center"
                                    item
                                    xs={12}
                                >
                                    <Grid item xs={6} md={6}>
                                        <TextField
                                            type="text"
                                            name="toGoOrderConfirmationMessage"
                                            placeholder="Enter message here"
                                            label="To-Go Order Confirmation Message"
                                            fullWidth
                                            variant="filled"
                                            InputProps={{
                                                disableUnderline: true,
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                    </form>
                )}
            />
        </div>
    );
}

const mapStateToProps = (state: RootState) => {
    return {
        restaurantCode: state.restaurant.selectedRestaurant?.restaurantCode,
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        updatePrimaryRestaurant: (
            data: RestaurantUpdateDetails & CallbackFunction
        ) => dispatch(updatePrimaryRestaurant(data)),
        updateRestaurantSettings: (
            data: RestaurantSettings & CallbackFunction
        ) => dispatch(updateRestaurantSettings(data)),
        updateRestaurantContactInfo: (
            data: RestaurantContactInfo & CallbackFunction
        ) => dispatch(updateRestaurantContactInfo(data)),
        updateRestaurantBranding: (
            data: RestaurantBranding & CallbackFunction
        ) => dispatch(updateRestaurantBranding(data)),
    };
};

type Props = ConnectedProps<typeof connected>;

const connected = connect(mapStateToProps, mapDispatchToProps);

export default connected(RestaurantSetting);
